import React from 'react';
import { MarketingPage, MarketingContent } from '../components/MarketingPage';

const content: MarketingContent = {
  seo: {
    title: 'A Private Alternative to Cloud Storage — Keep Your Files, Drop the Subscription',
    description:
      'Stop renting space for files you already own. Local Area Cloud keeps your photos and files on your own devices — private, cross-platform, about $10 a year flat instead of $100+ a year to iCloud, Google One, or Dropbox.',
    path: '/cloud-storage-alternative',
  },
  eyebrow: 'Own your files',
  h1: <>The alternative to <span className="em">cloud-storage rent.</span></>,
  lead:
    'iCloud, Google One, Dropbox, OneDrive — you pay every single month to keep your own photos and files on someone else’s computer. Local Area Cloud keeps them on your own devices instead: private, fast, cross-platform, and a flat price you actually own.',
  sections: [
    {
      h2: 'You’re renting space for files you already own',
      paras: [
        'The big cloud drives sell you a subscription, then quietly raise the floor: the free tier fills in a week, the upsell arrives, and from then on you pay forever just to store the photos and files that already live on your phone and laptop. Stop paying and your data is held hostage behind a paywall.',
        'Local Area Cloud flips the model. Your files stay on your own devices, move directly between them at local-network speed, and never touch a third-party server. You pay once — or nothing at all — and the storage is simply your own disks.',
      ],
    },
    {
      h2: 'What you’d pay this year — and every year after',
      paras: [
        'Here’s the going rate for the plan most people end up on (roughly 2 TB), versus Local Area Cloud:',
      ],
      table: {
        head: ['Service', 'Typical plan', 'Cost per year'],
        rows: [
          ['iCloud+', '2 TB', '≈ $120'],
          ['Google One', '2 TB', '≈ $100'],
          ['Dropbox Plus', '2 TB', '≈ $120–144'],
          ['Microsoft 365', '1 TB (with Office)', '≈ $70'],
          [{ v: 'Local Area Cloud', kind: 'yes' }, { v: 'Unlimited devices · your own disks', kind: 'yes' }, { v: '$0 free · ~$10 Pro', kind: 'yes' }],
        ],
      },
    },
    {
      h2: 'Feature by feature',
      paras: [
        'The honest side-by-side — including the one thing a remote cloud still does that a device-to-device tool doesn’t:',
      ],
      table: {
        head: ['', 'Local Area Cloud', 'Cloud-storage subscription'],
        rows: [
          ['Where your files live', { v: 'Your own devices', kind: 'yes' }, { v: 'The provider’s servers', kind: 'no' }],
          ['Ongoing cost', { v: 'None — ~$10/yr Pro', kind: 'yes' }, { v: '$8–12/mo, forever', kind: 'no' }],
          ['Storage limit', 'Your devices’ disks', 'Only what you rent'],
          ['Private — never scanned', { v: 'Never reaches us', kind: 'yes' }, { v: 'Scanned & indexed', kind: 'no' }],
          ['Transfer speed', { v: 'Local-network fast', kind: 'yes' }, { v: 'Upload / download over the internet', kind: 'no' }],
          ['Apple + Android + PC together', { v: 'Yes — every platform', kind: 'yes' }, { v: 'Often ecosystem-locked', kind: 'no' }],
          ['Reach devices away from home', 'Your own VPN (Pro)', 'Yes, built in'],
          ['Off-site copy if every device is lost', 'Pair a device / keep one always-on', { v: 'Yes', kind: 'yes' }],
          ['Data mining / ads', { v: 'None', kind: 'yes' }, { v: 'Varies by provider', kind: 'no' }],
          ['Vendor lock-in', { v: 'None — your disks', kind: 'yes' }, { v: 'Subscription + export friction', kind: 'no' }],
        ],
      },
    },
    {
      h2: 'The honest part: what a cloud vault still does better',
      paras: [
        'We won’t pretend otherwise: a remote cloud keeps an off-site copy, so if every one of your devices is lost, stolen, or destroyed at once, the provider still has your data. That’s the one real advantage of paying rent.',
        'Local Area Cloud closes most of that gap two ways. Its scheduled backups copy your files to another of your own devices automatically, and its built-in VPN lets you reach them from anywhere — still with no third-party cloud in the middle. For true disaster cover you can keep one always-on device (a home PC or a cheap mini-PC), or keep a provider’s free tier for that thin last resort while Local Area Cloud carries the bulk for free.',
      ],
    },
    {
      h2: 'Most of your cloud bill is just your camera roll',
      paras: [
        'Be honest about what fills that 2 TB: it’s photos and videos. Local Area Cloud’s Automatic Backup mirrors your camera roll to your own PC over Wi-Fi while your phone charges — full quality, nothing downscaled, no monthly fee. The single most expensive reason people pay for cloud storage becomes something your own devices just do overnight.',
      ],
    },
  ],
  faqs: [
    {
      q: 'Is Local Area Cloud a replacement for iCloud, Google Drive, or Dropbox?',
      a: 'For the jobs most people actually pay for — backing up photos, moving files between devices, and reaching them across platforms — yes. The one thing a paid cloud still does better is keep an off-site copy if all of your devices are lost at once; you can cover that by keeping one always-on device or a small free cloud tier.',
    },
    {
      q: 'Do I have to keep paying every month?',
      a: 'No. Local Area Cloud is free for up to two devices with every feature. Pro is about $10 a year (or $1.50/month) for unlimited devices and cross-network reach — a flat price, not a recurring storage rental.',
    },
    {
      q: 'Where do my files actually live?',
      a: 'On your own devices. Local Area Cloud’s cloud service is only a registry that helps your devices find each other; your file contents never reach our servers.',
    },
    {
      q: 'What happens to my files if I stop paying?',
      a: 'Nothing — they’re already on your own devices. There’s no provider account holding your data hostage behind a paywall.',
    },
    {
      q: 'Can I still reach my files when I’m away from home?',
      a: 'Yes. Pro includes a built-in WireGuard VPN so you can reach your devices from anywhere, with files still travelling directly between them rather than through a third-party cloud.',
    },
  ],
  related: [
    { label: 'iCloud alternative', to: '/icloud-alternative', note: 'Keep your photos, drop the bill' },
    { label: 'File sharing without the cloud', to: '/file-sharing-without-the-cloud', note: 'How your files stay private' },
    { label: 'Transfer files between phone & PC', to: '/transfer-files-phone-to-pc', note: 'The phone ⇄ PC job' },
  ],
  ctaBand: {
    h2: 'Stop renting space for your own files.',
    p: 'Keep them on your devices — private, fast, and yours. Free for two devices; about $10 a year for your whole fleet.',
  },
};

export const CloudStorageAlternative: React.FC = () => <MarketingPage content={content} />;
export default CloudStorageAlternative;
